import { useEffect, useState } from 'react';
import {
  Users, CreditCard, TrendingUp, DollarSign, AlertCircle, RefreshCw, Repeat,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { BarSeries } from '../../components/portal/AppTabs';
import { customerPortalApi, type BillingAnalytics } from '../../api/customerPortal';

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Kpi({ icon: Icon, tone, value, label }: { icon: typeof Users; tone: string; value: string | number; label: string }) {
  return (
    <Card className="p-3 lg:p-4">
      <div className="flex items-center gap-3">
        <div className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg sm:flex ${tone}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="truncate text-xl font-bold text-slate-950 dark:text-slate-100 lg:text-2xl">{value}</div>
          <div className="truncate text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function OverviewApp() {
  const [data, setData] = useState<BillingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    setError('');
    try {
      setData(await customerPortalApi.billingAnalytics(d));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load analytics.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(days); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) {
    return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;
  }

  const o = data?.overview;
  const series = data?.revenue_series ?? [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <Kpi icon={Users} tone="bg-blue-500/10 text-blue-500" value={o?.total_users ?? 0} label="Total Users" />
        <Kpi icon={CreditCard} tone="bg-green-500/10 text-green-500" value={o?.active_subscriptions ?? 0} label="Active Subscriptions" />
        <Kpi icon={DollarSign} tone="bg-amber-500/10 text-amber-500" value={money(o?.revenue_cents ?? 0)} label="Revenue (Processed)" />
        <Kpi icon={Repeat} tone="bg-purple-500/10 text-purple-500" value={money(o?.mrr_cents ?? 0)} label="MRR (active)" />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <Kpi icon={TrendingUp} tone="bg-emerald-500/10 text-emerald-500" value={money(o?.revenue_this_month_cents ?? 0)} label="Revenue This Month" />
        <Kpi icon={CreditCard} tone="bg-slate-500/10 text-slate-500" value={o?.payment_count ?? 0} label="Payments" />
        <Kpi icon={DollarSign} tone="bg-sky-500/10 text-sky-500" value={money(o?.average_payment_cents ?? 0)} label="Avg Payment" />
        <Kpi icon={AlertCircle} tone="bg-red-500/10 text-red-500" value={o?.past_due_subscriptions ?? 0} label="Past Due" />
      </div>

      <Card className="p-5">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Revenue</h2>
            <p className="text-sm text-slate-500">Daily processed payments.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button variant="secondary" size="sm" onClick={() => load()} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
        {!o?.revenue_verified && (
          <div className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            No processed payments yet — figures below are zero until the first successful payment is recorded.
          </div>
        )}
        <BarSeries
          points={series.map(p => ({ label: p.date.slice(5), value: p.revenue_cents / 100 }))}
          label="Daily revenue (USD)"
        />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-4 text-base font-semibold text-slate-950 dark:text-slate-100">Subscriptions by Plan</h2>
          {(data?.subscriptions.by_plan.length ?? 0) === 0 && <p className="text-sm text-slate-500">No subscriptions yet.</p>}
          <div className="space-y-1">
            {data?.subscriptions.by_plan.map(p => (
              <div key={p.plan_key} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-sm text-slate-700 dark:text-slate-300">{p.plan_name}</span>
                <span className="text-sm font-medium text-slate-950 dark:text-slate-100">{p.count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-base font-semibold text-slate-950 dark:text-slate-100">Product Sales</h2>
          {(data?.product_sales.length ?? 0) === 0 && <p className="text-sm text-slate-500">No paid invoices yet.</p>}
          <div className="space-y-1">
            {data?.product_sales.map(s => (
              <div key={s.product} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-sm text-slate-700 dark:text-slate-300">{s.product}</span>
                <span className="text-sm text-slate-500">{s.units} · <span className="font-medium text-slate-950 dark:text-slate-100">{money(s.revenue_cents)}</span></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h2 className="mb-4 text-base font-semibold text-slate-950 dark:text-slate-100">Subscriptions by Status</h2>
        {(data?.subscriptions.by_status.length ?? 0) === 0 && <p className="text-sm text-slate-500">No subscriptions yet.</p>}
        <div className="flex flex-wrap gap-3">
          {data?.subscriptions.by_status.map(s => (
            <div key={s.status} className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
              <div className="text-lg font-bold text-slate-950 dark:text-slate-100">{s.count}</div>
              <div className="text-xs capitalize text-slate-500">{s.status}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
